import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import { toastError, toastSuccess } from '../utils/toast';
import homeGradBackground from '../assets/homegrad.png';
import logoIso from '../assets/landing-page/logo-iso.svg';
import logoSoc2 from '../assets/landing-page/soc2-logo 1.svg';
import logoUpWealth from '../assets/logo-testimonials/company/company/company-logo/up-wealth.svg';
import logoHashguard from '../assets/logo-testimonials/company/company/company-logo/hashguard.svg';
import logoTheField from '../assets/logo-testimonials/company/company/company-logo/the-field.svg';
import logoKrishiv from '../assets/logo-testimonials/company/company/company-logo/krishiv.svg';
import logoTiksom from '../assets/logo-testimonials/company/company/company-logo/tiksom.svg';
import logoCovaloris from '../assets/logo-testimonials/company/company/company-logo/covaloris.svg';
import logoPinata from '../assets/logo-testimonials/company/company/company-logo/pinata.svg';
import logoAdvisor from '../assets/logo-testimonials/company/company/company-logo/advisor.svg';
import logoBenchling from '../assets/logo-testimonials/company/company/company-logo/benchling.svg';
import avatarDarrenMoon from '../assets/logo-testimonials/company/company/company-logo/advisor/darren-moon.svg';
import avatarSteeveDumont from '../assets/logo-testimonials/company/company/company-logo/advisor/steeve-dumont.svg';
import avatarFlorianBenejean from '../assets/logo-testimonials/company/company/company-logo/advisor/florian-benejean.svg';
import avatarBharatPrajapati from '../assets/logo-testimonials/company/company/company-logo/advisor/bharat-prajapati.svg';
import avatarMuhammadJButt from '../assets/logo-testimonials/company/company/company-logo/advisor/muhammad-j-butt.svg';
import avatarRocioCruz from '../assets/logo-testimonials/company/company/company-logo/advisor/rocio-cruz.svg';
import avatarCraigPady from '../assets/logo-testimonials/company/company/company-logo/advisor/craig-pady.svg';
import avatarOrionParrott from '../assets/logo-testimonials/company/company/company-logo/advisor/orion-parrott.svg';
import avatarNielsenKirby from '../assets/logo-testimonials/company/company/company-logo/advisor/nielsen-kirby.svg';

// FAQ Component
interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  toggleOpen: () => void;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, isOpen, toggleOpen }) => {
  return (
    <div className="border-b border-gray-200 py-6">
      <button
        className="w-full flex justify-between items-center text-left focus:outline-none"
        onClick={toggleOpen}
      >
        <span className="text-base font-medium text-gray-900">{question}</span>
        <span className="ml-6 flex-shrink-0">
          <svg
            className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {isOpen && (
        <div className="mt-3 pr-12">
          <div className="text-sm text-gray-600 leading-relaxed">{answer}</div>
        </div>
      )}
    </div>
  );
};

const FAQ: React.FC<{ onRequestDemo?: () => void }> = ({ onRequestDemo }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqItems: { question: string; answer: React.ReactNode }[] = [
    {
      question: 'Is Flashgate free?',
      answer: (
        <div className="space-y-3">
          <p>Yes. Flashgate includes a Free tier that lets individual users access Flashgate to connect and route their cloud and AI resources.</p>
          <p>If you own a domain or you're part of a team, you can switch to an Organization mode, which unlocks additional services such as Organization Management and Private Chat (with governance and policy controls).</p>
        </div>
      ),
    },
    {
      question: 'Can I change my plan later?',
      answer: (
        <div className="space-y-3">
          <p>Yes. You can upgrade your plan at any time.</p>
          <p>Upgrading gives you access to the full credit allocation included in the new plan.<br/>Downgrading does not remove your remaining credits — credits stay attached to your account.</p>
          <p>We can show exactly how this works during a demo.<br/>To access paid plans today, you'll first need to <button type="button" onClick={(e) => { e.stopPropagation(); onRequestDemo?.(); }} className="text-[#617BFF] font-semibold hover:underline bg-transparent border-none cursor-pointer p-0 inline">Request a demo</button>.</p>
        </div>
      ),
    },
    {
      question: 'How does Flashgate work?',
      answer: (
        <div className="space-y-3">
          <p>Flashgate is a unified control plane for cloud + AI, built around three core services:</p>
          <div className="space-y-2">
            <p><strong>Flashgate</strong><br/>A single gateway endpoint that lets you connect providers and route traffic with policies (cost, performance, governance), while keeping your integrations simple.</p>
            <p><strong>Repositories</strong><br/>A way to bundle resources (AI models, storage buckets, and more) into one logical unit, with a unified access layer.</p>
            <p><strong>Organization & Workspaces</strong><br/>Team-level management for members, access, governance, and service configuration across your repositories.</p>
          </div>
        </div>
      ),
    },
    {
      question: 'What resources can I connect to the Gateway?',
      answer: (
        <div className="space-y-3">
          <p>Today you can connect:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>AI LLM providers (models you bring or enable through Flashgate)</li>
            <li>Cloud storage buckets (for object storage routing and management)</li>
          </ul>
          <p>And we're expanding beyond this over time as new resource types are added to the platform.</p>
        </div>
      ),
    },
    {
      question: 'What is a Repository in Flashgate?',
      answer: (
        <div className="space-y-3">
          <p>A Repository is where you attach and manage resources like:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>AI LLMs</li>
            <li>Storage buckets (and more in the future)</li>
          </ul>
          <p>Each repository can generate API keys that grant access to all resources attached to that repository.</p>
          <p>Most importantly, repositories are <strong>compatible with existing provider SDKs</strong>.<br/>That means you can keep using a familiar SDK (for example an AWS SDK) while, with a Flashgate key, routing to other providers and services behind the scenes (cloud or AI).</p>
        </div>
      ),
    },
    {
      question: 'What is Private Chat?',
      answer: (
        <div className="space-y-3">
          <p>Private Chat is a secure chat experience designed for teams that need governance and policy controls.</p>
          <p>It allows you to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Select a Repository</li>
            <li>Chat with the AI models connected to that repository</li>
            <li>Apply policy controls around usage, behavior, and access</li>
          </ul>
          <p>Flashgate does not retain your content: data transits through Bridge Nodes to the underlying providers, so you can keep control of where requests go.</p>
        </div>
      ),
    },
    {
      question: 'What is a Bridge Node?',
      answer: (
        <div className="space-y-3">
          <p>A Bridge Node is the gateway component that translates and routes the requests your application sends via standard SDKs to the right underlying provider.</p>
          <p>Flashgate can operate with:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Public Bridge Nodes</li>
            <li>Private Bridge Nodes (for stricter control or specific environments)</li>
          </ul>
          <p><strong>Example:</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li>If your app uses an AWS SDK, the Bridge Node can route those calls to different backends depending on your configuration (including other cloud providers).</li>
            <li>If you use an OpenAI-style SDK, Flashgate can route requests to other model providers (e.g., Anthropic- or Google/Gemini-compatible endpoints), based on your policies and available resources.</li>
          </ul>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-1">
      {faqItems.map((item, index) => (
        <FAQItem
          key={index}
          question={item.question}
          answer={item.answer}
          isOpen={openIndex === index}
          toggleOpen={() => setOpenIndex(openIndex === index ? null : index)}
        />
      ))}
    </div>
  );
};

// Q&A Interactive Flow Data
interface QAFlow {
  question: string;
  response1: string;
  response2: string;
  choices: string[];
  followUpGeneric: string;
  response3: string;
}

const qaFlows: QAFlow[] = [
  {
    question: 'How can Flashgate help with cloud infrastructure?',
    response1: 'Flashgate acts as a control and integration layer across your existing cloud providers, without replacing them. You connect your cloud storage buckets and/or LLM endpoints using Bring-Your-Own-Key (BYOK), and expose them through a unified gateway interface. This standardizes access patterns, permissions, and operational visibility across providers. Flashgate does not deploy infrastructure; it improves integration, governance, and cloud/AI operations. Do you want to know more about it?',
    response2: "In practice, teams point their storage (S3, GCS, or Blob) or OpenAI-compatible clients to Flashgate's gateway endpoint to simplify integrations and apply consistent access rules. This makes it easier to manage keys, scopes, and policies per environment or team, while keeping a single operational view. Which scenario fits you best right now?",
    choices: ['Storage', 'LLMs', 'Both'],
    followUpGeneric: "Awesome \u2014 we'd be happy to go further on this use case with you. If you'd like, we can walk through a quick demo together and map Flashgate to your current setup.",
    response3: 'If you share a bit of context (providers, teams, environments), we can show the cleanest integration path for your setup. Request a demo to see a guided walkthrough tailored to your use case.',
  },
  {
    question: 'How does Flashgate support building a resilient and scalable cloud and AI strategy?',
    response1: 'Flashgate supports resilience and scalability by standardizing integration through a unified gateway across multiple storage backends and/or LLM providers while keeping BYOK. You gain consistent governance (policies, keys, scopes) and cross-vendor observability (usage, latency, health), which reduces operational risk and provider lock-in. Flashgate does not deploy infrastructure; it provides control, integration, and ops capabilities on top of what you already run. Do you want to know more about it?',
    response2: 'Concretely, you can structure access by team or environment using repositories and enforce consistent rules while tracking usage in one place, even when the underlying providers differ. This approach makes it easier to evolve your stack (add a provider, change a backend, absorb traffic spikes) without rewriting application integrations. Would you like to see an example?',
    choices: ['LLMs', 'Storage', 'Hybrid setup'],
    followUpGeneric: "Awesome \u2014 we'd be happy to go further on this use case with you. If you'd like, we can walk through a quick demo together and map Flashgate to your current setup.",
    response3: "If you tell us your current providers and operational goals (continuity, scaling, governance), we can propose a clean setup pattern. Request a demo and we'll walk through it on an architecture close to yours.",
  },
  {
    question: 'Best practices for cloud-of-clouds management?',
    response1: 'Best practices include standardizing integrations, centralizing observability, and enforcing consistent access policies across providers. Flashgate is positioned on integration + governance + ops visibility, rather than infrastructure deployment or orchestration. The goal is to operate multiple backends as one coherent system from an access and operations perspective. Do you want to know more about it?',
    response2: 'A simple pattern is to create repositories by environment (dev/staging/prod) or by team, then control access using scoped keys and policies. This clarifies who can use what, reduces ad-hoc integrations, and improves operational control. Do you prefer structuring by team, by product, or by environment?',
    choices: ['By team', 'By product', 'By environment'],
    followUpGeneric: "Awesome \u2014 we'd be happy to go further on this use case with you. If you'd like, we can walk through a quick demo together and map Flashgate to your current setup.",
    response3: 'If you describe your org structure and providers, we can recommend a repository/policy layout that matches how you operate. Request a demo to see the setup and the ops views end-to-end.',
  },
  {
    question: 'How to get better control over cloud and AI operations?',
    response1: "Flashgate improves operational control by standardizing access, policies, and permissions, with unified visibility across connected providers. On the AI side, you connect LLM endpoints using BYOK and enforce consistent rules by team or project. On the storage side, you connect buckets and apply guardrails and access controls consistently. Flashgate focuses on integration, governance, and ops observability \u2014 not deploying your infrastructure. Do you want to know more about it?",
    response2: 'The key is that Flashgate complements tools like Terraform/Kubernetes rather than replacing them: it gives you a unified gateway and an ops layer to monitor and govern usage across providers. If you want to test quickly, we can start with one bucket and one LLM endpoint and validate behavior with scoped keys. Would you rather begin from an existing storage bucket or an existing LLM endpoint?',
    choices: ['Storage bucket', 'LLM endpoint'],
    followUpGeneric: "Awesome \u2014 we'd be happy to go further on this use case with you. If you'd like, we can walk through a quick demo together and map Flashgate to your current setup.",
    response3: "If your priority is ops + governance across teams and environments, a demo is usually the fastest way to confirm fit. Request a demo and we'll guide you through a configuration aligned with your organization.",
  },
];

const LandingPageV2: React.FC = () => {
  const navigate = useNavigate();
  const { apiClient } = useApi();
  const [isLoading, setIsLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState<{id: string; role: 'user'|'assistant'; content: string}[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [storedQuestion, setStoredQuestion] = useState('');
  const [showDemoModal, setShowDemoModal] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Q&A Flow state
  const [disabledQuestions, setDisabledQuestions] = useState<Set<number>>(new Set());
  const [flowStage, setFlowStage] = useState<'idle'|'yes-no'|'choices'|'done'>('idle');
  const [currentQIndex, setCurrentQIndex] = useState<number|null>(null);
  const [showPokeModal, setShowPokeModal] = useState(false);
  const [pokeMessage, setPokeMessage] = useState('');

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, isTyping, flowStage]);

  // Handle clicking a predefined question box
  const handleQuestionClick = (index: number) => {
    if (disabledQuestions.has(index) || isTyping) return;

    const flow = qaFlows[index];
    setDisabledQuestions(prev => new Set(prev).add(index));
    setCurrentQIndex(index);
    setFlowStage('idle');
    setStoredQuestion(flow.question);

    setChatMessages(prev => [...prev, { id: Date.now().toString(), role: 'user', content: flow.question }]);

    setIsTyping(true);
    setTimeout(() => {
      setChatMessages(prev => [...prev, { id: (Date.now() + 1).toString(), role: 'assistant', content: flow.response1 }]);
      setIsTyping(false);
      setFlowStage('yes-no');
    }, 1200);
  };

  // Handle Yes/No after Response 1
  const handleYesNo = (answer: 'yes' | 'no') => {
    if (currentQIndex === null) return;

    setChatMessages(prev => [...prev, { id: Date.now().toString(), role: 'user', content: answer === 'yes' ? 'Yes' : 'No' }]);

    if (answer === 'no') {
      setFlowStage('done');
      setCurrentQIndex(null);
      return;
    }

    const flow = qaFlows[currentQIndex];
    setIsTyping(true);
    setFlowStage('idle');
    setTimeout(() => {
      setChatMessages(prev => [...prev, { id: (Date.now() + 1).toString(), role: 'assistant', content: flow.response2 }]);
      setIsTyping(false);
      setFlowStage('choices');
    }, 1200);
  };

  // Handle choice selection after Response 2
  const handleChoiceClick = (choice: string) => {
    if (currentQIndex === null) return;

    setChatMessages(prev => [...prev, { id: Date.now().toString(), role: 'user', content: choice }]);

    const flow = qaFlows[currentQIndex];
    setPokeMessage(flow.followUpGeneric);
    setShowPokeModal(true);
    setFlowStage('done');
  };

  // Handle free-text input (any non-predefined question)
  const handleFreeTextSend = (message: string) => {
    if (!message.trim()) return;

    setChatMessages(prev => [...prev, { id: Date.now().toString(), role: 'user', content: message }]);
    setInputValue('');
    setStoredQuestion(message);

    setShowSignupModal(true);
  };

  // Handle poke popup actions
  const handlePokeRequestDemo = () => {
    setShowPokeModal(false);
    setShowDemoModal(true);
  };

  const handlePokeCancel = () => {
    setShowPokeModal(false);

    if (currentQIndex !== null) {
      const flow = qaFlows[currentQIndex];
      setChatMessages(prev => [...prev, { id: Date.now().toString(), role: 'assistant', content: flow.response3 }]);
    }

    setCurrentQIndex(null);
  };

  const handleRequestDemo = async () => {
    const win = window.open('', '_blank'); // open in same turn as click to avoid popup blocker
    setIsLoading(true);
    try {
      if (apiClient) {
        const response = await apiClient.requestDemo({
          userId: '',
          email: '',
          name: '',
        });
        if (response?.success && response?.calendarUrl) {
          if (win) win.location.href = response.calendarUrl;
          else window.open(response.calendarUrl, '_blank');
        } else {
          if (win) win.close();
          toastSuccess('Demo request sent! We\'ll be in touch soon.');
        }
      } else if (win) win.close();
    } catch (error) {
      if (win) win.close();
      toastError('Failed to send demo request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Hero Section with Chat */}
      <main className="flex-1">
        <div 
          className="min-h-[80vh] flex flex-col"
          style={{
            backgroundImage: `url(${homeGradBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {/* Header - inside hero section, not fixed */}
          <header className="w-full">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
              <div className="flex justify-between items-center h-20">
                {/* Logo */}
                <a 
                  href="https://flashback.tech" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center"
                >
                  <img src="/flashgate-logo-white.png" alt="Flashgate" className="h-8" />
                </a>

                {/* Navigation */}
                <nav className="flex items-center gap-2 sm:gap-4 md:gap-6">
                  <button
                    onClick={handleRequestDemo}
                    disabled={isLoading}
                    className="hidden sm:inline text-sm font-medium text-white/80 hover:text-white transition-colors disabled:opacity-50"
                  >
                    {isLoading ? 'Sending...' : 'Request a Demo'}
                  </button>
                  <a
                    href="https://docs.flashback.tech"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden md:inline text-sm font-medium text-white/80 hover:text-white transition-colors"
                  >
                    Docs
                  </a>
                  <button
                    onClick={() => navigate('/auth')}
                    className="text-sm font-medium text-white/80 hover:text-white transition-colors"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate('/auth?tab=signup')}
                    className="px-4 sm:px-5 py-2.5 text-sm font-semibold text-[#617BFF] bg-white rounded-full hover:bg-white/90 transition-all shadow-lg hover:shadow-xl"
                  >
                    Sign up
                  </button>
                </nav>
              </div>
            </div>
          </header>

          {/* Hero Content */}
          <div className="flex-1 flex items-center justify-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          {/* Hero Text */}
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-6xl font-bold text-white mb-4 tracking-tight">
              What do you need help with?
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto font-light leading-relaxed">
              Ask anything about AI integration, multi-cloud strategies, or resource monitoring. Flashgate gives you the controls you need.
            </p>
          </div>

          {/* Chat Interface - Modernized */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden max-w-2xl mx-auto transform transition-all hover:scale-[1.01] duration-500">
            {/* Chat Area */}
            <div className="p-6">
              {chatMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#F0F2FF] to-white rounded-2xl shadow-inner flex items-center justify-center mb-4">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2.5">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                  </div>
                  <p className="text-gray-600 text-base mb-4 font-medium">Ask about multi-cloud strategy, how Flashgate helps businesses get better control of cloud-of-clouds and AI</p>
                </div>
              ) : (
                <div ref={chatContainerRef} className="max-h-64 overflow-y-auto mb-4 space-y-3">
                  {chatMessages.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] px-4 py-2.5 rounded-xl text-sm leading-relaxed ${msg.role === 'user' ? 'bg-[#617BFF] text-white' : 'bg-gray-100 text-gray-700'}`}>
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 text-gray-500 px-4 py-2.5 rounded-xl text-sm">Typing...</div>
                    </div>
                  )}
                  {!isTyping && flowStage === 'yes-no' && (
                    <div className="flex justify-start gap-2 mt-2">
                      <button onClick={() => handleYesNo('yes')} className="px-5 py-2 text-sm font-medium bg-[#617BFF] text-white rounded-full hover:bg-[#5068E0] transition-all shadow-sm">Yes</button>
                      <button onClick={() => handleYesNo('no')} className="px-5 py-2 text-sm font-medium bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-all">No</button>
                    </div>
                  )}
                  {!isTyping && flowStage === 'choices' && currentQIndex !== null && (
                    <div className="flex flex-wrap justify-start gap-2 mt-2">
                      {qaFlows[currentQIndex].choices.map((choice, i) => (
                        <button key={i} onClick={() => handleChoiceClick(choice)} className="px-4 py-2 text-sm font-medium bg-white text-[#617BFF] border border-[#617BFF]/30 rounded-full hover:bg-[#F0F2FF] transition-all shadow-sm">
                          {choice}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Chat Input */}
              <div className="flex items-center gap-3 relative">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleFreeTextSend(inputValue)}
                  placeholder="Type your question about cloud or AI..."
                  className="flex-1 px-4 py-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#617BFF]/50 focus:border-[#617BFF] transition-all shadow-sm"
                />
                <button
                  onClick={() => handleFreeTextSend(inputValue)}
                  disabled={!inputValue.trim()}
                  className="absolute right-2 p-2 bg-[#617BFF] text-white rounded-lg hover:bg-[#5068E0] transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transform hover:-translate-y-0.5 active:translate-y-0"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Suggested Questions - Interactive Q&A Boxes */}
          <div className="mt-6 max-w-3xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {qaFlows.map((flow, index) => (
                <button
                  key={index}
                  onClick={() => handleQuestionClick(index)}
                  disabled={disabledQuestions.has(index) || isTyping}
                  className={`group px-4 py-3 text-sm text-left font-medium rounded-xl border shadow-lg transition-all duration-300 transform ${
                    disabledQuestions.has(index)
                      ? 'bg-gray-200/80 text-gray-400 border-gray-300/50 cursor-not-allowed shadow-none'
                      : 'text-gray-700 bg-white/95 backdrop-blur-sm border-white/40 hover:shadow-xl hover:bg-white hover:text-[#617BFF] hover:border-[#617BFF]/30 hover:-translate-y-1'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{flow.question}</span>
                    {!disabledQuestions.has(index) && (
                      <svg className="w-4 h-4 text-gray-400 group-hover:text-[#617BFF] opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-1 flex-shrink-0 ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 5l7 7-7 7"/>
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* CTA Below Chat - Modernized */}
          <div className="mt-12 text-center">
            <p className="text-white mb-2 text-xl font-semibold tracking-tight">Ready to improve your cloud infrastructure?</p>
            <p className="text-white/80 mb-6 text-base font-light">Best-in-class controls for cloud and AI workloads</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/auth?tab=signup')}
                className="px-8 py-3.5 text-sm font-semibold text-[#617BFF] bg-white rounded-full hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50"
              >
                Sign up for free
              </button>
              <button
                onClick={handleRequestDemo}
                disabled={isLoading}
                className="px-8 py-3.5 text-sm font-semibold text-white bg-white/10 border border-white/20 rounded-full hover:bg-white/20 transition-all backdrop-blur-sm hover:border-white/40"
              >
                {isLoading ? 'Sending...' : 'Request a Demo'}
              </button>
            </div>
          </div>
        </div>
          </div>
        </div>

        {/* Social Proof / Stats Section */}
        <div className="bg-white py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Title */}
            <h2 className="text-center" style={{ fontWeight: 600, letterSpacing: '-0.02em', color: '#181D27', marginBottom: '12px', fontSize: 'clamp(24px, 5vw, 36px)', lineHeight: 'clamp(32px, 5vw, 44px)' }}>
              Don't just take our word for it
            </h2>
            <p className="text-center px-2" style={{ fontWeight: 400, fontSize: 'clamp(16px, 3vw, 20px)', lineHeight: 'clamp(24px, 4vw, 30px)', color: '#535862', marginBottom: '48px' }}>
              Hear first-hand from our incredible community of customers.
            </p>

            {/* Stats */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-0" style={{ marginBottom: '48px' }}>
              <div className="flex-1 text-center">
                <div className="text-4xl sm:text-5xl lg:text-[60px]" style={{ fontWeight: 600, lineHeight: '1.2', letterSpacing: '-0.02em', color: '#617BFF' }}>
                  20+
                </div>
                <div className="text-sm sm:text-base lg:text-lg" style={{ fontWeight: 600, lineHeight: '28px', color: '#181D27' }}>
                  Companies onboard
                </div>
              </div>
              <div className="hidden sm:block" style={{ width: '1px', height: '80px', backgroundColor: '#E5E7EB' }} />
              <div className="block sm:hidden" style={{ width: '80px', height: '1px', backgroundColor: '#E5E7EB' }} />
              <div className="flex-1 text-center">
                <div className="text-4xl sm:text-5xl lg:text-[60px]" style={{ fontWeight: 600, lineHeight: '1.2', letterSpacing: '-0.02em', color: '#617BFF' }}>
                  40TB
                </div>
                <div className="text-sm sm:text-base lg:text-lg" style={{ fontWeight: 600, lineHeight: '28px', color: '#181D27' }}>
                  Monthly data traffic
                </div>
              </div>
              <div className="hidden sm:block" style={{ width: '1px', height: '80px', backgroundColor: '#E5E7EB' }} />
              <div className="block sm:hidden" style={{ width: '80px', height: '1px', backgroundColor: '#E5E7EB' }} />
              <div className="flex-1 text-center">
                <div className="text-4xl sm:text-5xl lg:text-[60px]" style={{ fontWeight: 600, lineHeight: '1.2', letterSpacing: '-0.02em', color: '#617BFF' }}>
                  200M
                </div>
                <div className="text-sm sm:text-base lg:text-lg" style={{ fontWeight: 600, lineHeight: '28px', color: '#181D27' }}>
                  Monthly token traffic
                </div>
              </div>
            </div>

            {/* Compliance text */}
            <p className="text-center px-2" style={{ fontWeight: 400, fontSize: 'clamp(14px, 3vw, 20px)', lineHeight: 'clamp(22px, 4vw, 30px)', color: '#535862', marginBottom: '32px' }}>
              Building Flashgate to meet the highest security and compliance standards
            </p>

            {/* Logos */}
            <div className="flex items-center justify-center gap-6 sm:gap-8">
              <img src={logoIso} alt="ISO 27001" className="h-14 sm:h-20" />
              <img src={logoSoc2} alt="SOC 2" className="h-14 sm:h-20" />
            </div>
          </div>
        </div>

        {/* Testimonials Marquee */}
        <style>{`
          @keyframes scrollDown {
            0% { transform: translateY(0); }
            100% { transform: translateY(-50%); }
          }
          @keyframes scrollUp {
            0% { transform: translateY(-50%); }
            100% { transform: translateY(0); }
          }
        `}</style>
        <div className="bg-white pb-16 sm:pb-24">
          <div
            className="max-w-[1248px] mx-auto px-4 sm:px-6 lg:px-8"
            style={{
              height: '700px',
              overflow: 'hidden',
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)',
            }}
          >
            <div className="hidden lg:grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: '24px', height: '100%' }}>

              {/* Column 1 - Scrolls Down */}
              <div style={{ overflow: 'hidden', height: '100%' }}>
                <div style={{ animation: 'scrollDown 30s linear infinite', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {[0, 1].map(clone => (
                    <div key={clone} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      {/* Card 1 - Darren Moon */}
                      <div className="p-6" style={{ borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <img src={logoUpWealth} alt="UpWealth" style={{ height: '28px', marginBottom: '20px' }} />
                          <p style={{ fontSize: '15px', lineHeight: '24px', color: '#535862', marginBottom: '24px' }}>
                            Flashgate gives UpWealth clear governance, visibility, and cost control across cloud and AI operations without slowing product delivery. It's ideal when you need provider flexibility and strong operational discipline in a regulated, security-first environment.
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <img src={avatarDarrenMoon} alt="Darren Moon" style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0 }} />
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#181D27' }}>Darren Moon</span>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#617BFF"/><path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span style={{ fontSize: '13px', color: '#667085' }}>Executive Chairman and Founder, UpWealth</span>
                          </div>
                        </div>
                      </div>
                      {/* Card 4 - Steeve Dumont */}
                      <div className="p-6" style={{ borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <img src={logoHashguard} alt="Hashguard" style={{ height: '28px', marginBottom: '20px' }} />
                          <p style={{ fontSize: '15px', lineHeight: '24px', color: '#535862', marginBottom: '24px' }}>
                            Flashgate helped Hashguard plug its decentralized storage into the same interface as AWS/GCP/Azure in days, not months. We now route customer traffic with policies, telemetry, and cost controls through one gateway, without rewriting our SDK integrations.
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <img src={avatarSteeveDumont} alt="Steeve Dumont" style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0 }} />
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#181D27' }}>Steeve Dumont</span>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#617BFF"/><path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span style={{ fontSize: '13px', color: '#667085' }}>Co-founder & CEO, Hashguard</span>
                          </div>
                        </div>
                      </div>
                      {/* Card 7 - Florian Benejean */}
                      <div className="p-6" style={{ borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <img src={logoTheField} alt="The Field" style={{ height: '28px', marginBottom: '20px' }} />
                          <p style={{ fontSize: '15px', lineHeight: '24px', color: '#535862', marginBottom: '24px' }}>
                            Flashgate brings governance, visibility, and cost control to infrastructure operations without slowing teams down. It's a strong fit for sovereign, multi-entity environments where policies and provider flexibility matter.
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <img src={avatarFlorianBenejean} alt="Florian Benejean" style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0 }} />
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#181D27' }}>Florian Benejean</span>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#617BFF"/><path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span style={{ fontSize: '13px', color: '#667085' }}>CTO / CIO, The Field</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Column 2 - Scrolls Up */}
              <div style={{ overflow: 'hidden', height: '100%' }}>
                <div style={{ animation: 'scrollUp 30s linear infinite', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {[0, 1].map(clone => (
                    <div key={clone} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      {/* Card 2 - Bharat Prajapati */}
                      <div className="p-6" style={{ borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <img src={logoKrishiv} alt="Krishiv Innovations" style={{ height: '28px', marginBottom: '20px' }} />
                          <p style={{ fontSize: '15px', lineHeight: '24px', color: '#535862', marginBottom: '24px' }}>
                            Flashgate gives Krishiv Innovations a clean, repeatable way to govern cloud and AI operations for our clients. It helps us to deliver scalable, secure solutions faster, while keeping provider flexibility for global clients.
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <img src={avatarBharatPrajapati} alt="Bharat Prajapati" style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0 }} />
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#181D27' }}>Bharat Prajapati</span>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#617BFF"/><path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span style={{ fontSize: '13px', color: '#667085' }}>CTO, Krishiv Innovations</span>
                          </div>
                        </div>
                      </div>
                      {/* Card 5 - Muhammad J. Butt */}
                      <div className="p-6" style={{ borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <img src={logoTiksom} alt="Tiksom" style={{ height: '28px', marginBottom: '20px' }} />
                          <p style={{ fontSize: '15px', lineHeight: '24px', color: '#535862', marginBottom: '24px' }}>
                            Flashgate gives Tiksom a clean control layer for visibility, guardrails, and cost control, so delivery teams can focus on product outcomes. It's a strong fit for MVP-to-scale engagements where clients need provider flexibility without operational complexity.
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <img src={avatarMuhammadJButt} alt="Muhammad J. Butt" style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0 }} />
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#181D27' }}>Muhammad J. Butt</span>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#617BFF"/><path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span style={{ fontSize: '13px', color: '#667085' }}>Co-Founder and COO, Tiksom</span>
                          </div>
                        </div>
                      </div>
                      {/* Card 8 - Rocio Cruz */}
                      <div className="p-6" style={{ borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <img src={logoCovaloris} alt="Covaloris" style={{ height: '28px', marginBottom: '20px' }} />
                          <p style={{ fontSize: '15px', lineHeight: '24px', color: '#535862', marginBottom: '24px' }}>
                            Flashgate gives Covaloris a clean control layer for AI operations (visibility, guardrails, and cost control) so teams can focus on delivering real-world outcomes. It's a practical way to industrialize AI projects with more consistency and less operational friction.
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <img src={avatarRocioCruz} alt="Rocio Cruz" style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0 }} />
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#181D27' }}>Rocio Cruz</span>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#617BFF"/><path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span style={{ fontSize: '13px', color: '#667085' }}>COO, Covaloris</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Column 3 - Scrolls Down */}
              <div style={{ overflow: 'hidden', height: '100%' }}>
                <div style={{ animation: 'scrollDown 30s linear infinite', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {[0, 1].map(clone => (
                    <div key={clone} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      {/* Card 3 - Craig Pady */}
                      <div className="p-6" style={{ borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <img src={logoPinata} alt="Pinata" style={{ height: '28px', marginBottom: '20px' }} />
                          <p style={{ fontSize: '15px', lineHeight: '24px', color: '#535862', marginBottom: '24px' }}>
                            Flashgate makes it effortless for developers to use Pinata's IPFS storage through a familiar S3-compatible interface. It lowers adoption friction and adds governance and visibility, so teams can build and share files on IPFS with the same workflows they already use for cloud storage.
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <img src={avatarCraigPady} alt="Craig Pady" style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0 }} />
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#181D27' }}>Craig Pady</span>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#617BFF"/><path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span style={{ fontSize: '13px', color: '#667085' }}>Senior Account Executive, Pinata</span>
                          </div>
                        </div>
                      </div>
                      {/* Card 6 - Orion Parrott */}
                      <div className="p-6" style={{ borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <img src={logoAdvisor} alt="Advisor" style={{ height: '28px', marginBottom: '20px' }} />
                          <p style={{ fontSize: '15px', lineHeight: '24px', color: '#535862', marginBottom: '24px' }}>
                            As an advisor, I like Flashgate's approach: it makes cloud and AI operations measurable and governable, without locking teams into a single vendor. It's a strong foundation for startups that need to scale fast, keep costs under control, and maintain the flexibility to switch providers as the product evolves.
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <img src={avatarOrionParrott} alt="Orion Parrott" style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0 }} />
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#181D27' }}>Orion Parrott</span>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#617BFF"/><path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span style={{ fontSize: '13px', color: '#667085' }}>Advisor</span>
                          </div>
                        </div>
                      </div>
                      {/* Card 9 - Nielsen Kirby Aragon */}
                      <div className="p-6" style={{ borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <img src={logoBenchling} alt="Benchling" style={{ height: '28px', marginBottom: '20px' }} />
                          <p style={{ fontSize: '15px', lineHeight: '24px', color: '#535862', marginBottom: '24px' }}>
                            Flashgate is a pragmatic control plane: it brings visibility, policy guardrails, and cost discipline. For enterprise ops teams, it's a strong way to scale AI usage and multi-provider infrastructure without adding operational chaos.
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <img src={avatarNielsenKirby} alt="Nielsen Kirby Aragon" style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0 }} />
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#181D27' }}>Nielsen Kirby Aragon</span>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#617BFF"/><path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span style={{ fontSize: '13px', color: '#667085' }}>Head of Business Technology</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Mobile: single column scrolling down */}
            <div className="lg:hidden" style={{ overflow: 'hidden', height: '100%' }}>
              <div style={{ animation: 'scrollDown 40s linear infinite', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[0, 1].map(clone => (
                  <div key={clone} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div className="p-5" style={{ borderRadius: '12px', border: '1px solid #E5E7EB' }}>
                      <p style={{ fontSize: '14px', lineHeight: '22px', color: '#535862', marginBottom: '16px' }}>Flashgate gives UpWealth clear governance, visibility, and cost control across cloud and AI operations without slowing product delivery.</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={avatarDarrenMoon} alt="Darren Moon" style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0 }} />
                        <div><span style={{ fontSize: '13px', fontWeight: 600, color: '#181D27' }}>Darren Moon</span><br/><span style={{ fontSize: '12px', color: '#667085' }}>UpWealth</span></div>
                      </div>
                    </div>
                    <div className="p-5" style={{ borderRadius: '12px', border: '1px solid #E5E7EB' }}>
                      <p style={{ fontSize: '14px', lineHeight: '22px', color: '#535862', marginBottom: '16px' }}>Flashgate gives Krishiv Innovations a clean, repeatable way to govern cloud and AI operations for our clients.</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={avatarBharatPrajapati} alt="Bharat Prajapati" style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0 }} />
                        <div><span style={{ fontSize: '13px', fontWeight: 600, color: '#181D27' }}>Bharat Prajapati</span><br/><span style={{ fontSize: '12px', color: '#667085' }}>Krishiv Innovations</span></div>
                      </div>
                    </div>
                    <div className="p-5" style={{ borderRadius: '12px', border: '1px solid #E5E7EB' }}>
                      <p style={{ fontSize: '14px', lineHeight: '22px', color: '#535862', marginBottom: '16px' }}>Flashgate makes it effortless for developers to use Pinata's IPFS storage through a familiar S3-compatible interface.</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={avatarCraigPady} alt="Craig Pady" style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0 }} />
                        <div><span style={{ fontSize: '13px', fontWeight: 600, color: '#181D27' }}>Craig Pady</span><br/><span style={{ fontSize: '12px', color: '#667085' }}>Pinata</span></div>
                      </div>
                    </div>
                    <div className="p-5" style={{ borderRadius: '12px', border: '1px solid #E5E7EB' }}>
                      <p style={{ fontSize: '14px', lineHeight: '22px', color: '#535862', marginBottom: '16px' }}>Flashgate helped Hashguard plug its decentralized storage into the same interface as AWS/GCP/Azure in days, not months.</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={avatarSteeveDumont} alt="Steeve Dumont" style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0 }} />
                        <div><span style={{ fontSize: '13px', fontWeight: 600, color: '#181D27' }}>Steeve Dumont</span><br/><span style={{ fontSize: '12px', color: '#667085' }}>Hashguard</span></div>
                      </div>
                    </div>
                    <div className="p-5" style={{ borderRadius: '12px', border: '1px solid #E5E7EB' }}>
                      <p style={{ fontSize: '14px', lineHeight: '22px', color: '#535862', marginBottom: '16px' }}>Flashgate gives Tiksom a clean control layer for visibility, guardrails, and cost control.</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={avatarMuhammadJButt} alt="Muhammad J. Butt" style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0 }} />
                        <div><span style={{ fontSize: '13px', fontWeight: 600, color: '#181D27' }}>Muhammad J. Butt</span><br/><span style={{ fontSize: '12px', color: '#667085' }}>Tiksom</span></div>
                      </div>
                    </div>
                    <div className="p-5" style={{ borderRadius: '12px', border: '1px solid #E5E7EB' }}>
                      <p style={{ fontSize: '14px', lineHeight: '22px', color: '#535862', marginBottom: '16px' }}>As an advisor, I like Flashgate's approach: it makes cloud and AI operations measurable and governable.</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={avatarOrionParrott} alt="Orion Parrott" style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0 }} />
                        <div><span style={{ fontSize: '13px', fontWeight: 600, color: '#181D27' }}>Orion Parrott</span><br/><span style={{ fontSize: '12px', color: '#667085' }}>Advisor</span></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-gray-50 py-16 sm:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 text-center mb-10">
              Everything you need to know about Flashgate.
            </p>
            <FAQ onRequestDemo={handleRequestDemo} />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start">
            {/* Logo & Description */}
            <div className="mb-8 md:mb-0">
              <a href="https://flashback.tech" target="_blank" rel="noopener noreferrer">
                <img src="/flashgate-logo-black.png" alt="Flashgate" className="h-8 mb-4 cursor-pointer hover:opacity-80 transition-opacity" />
              </a>
              <p className="text-sm text-gray-500 max-w-xs">
                The control layer for multi-cloud and AI infrastructure.
              </p>
            </div>

            {/* Links */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-4">Product</h3>
                <ul className="space-y-3">
                  <li>
                    <a href="https://docs.flashback.tech" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-900">
                      Documentation
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-4">Company</h3>
                <ul className="space-y-3">
                  <li>
                    <a href="https://flashback.tech/contact" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-900">
                      Contact
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-4">Social</h3>
                <ul className="space-y-3">
                  <li>
                    <a href="https://x.com/FlashbackSocial" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-900">
                      X (Twitter)
                    </a>
                  </li>
                  <li>
                    <a href="https://discord.com/invite/yy8kyM5qFB" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-900">
                      Discord
                    </a>
                  </li>
                  <li>
                    <a href="https://www.linkedin.com/company/flashback-network" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-900">
                      LinkedIn
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-10 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-400 text-center">
              © 2025 Flashgate. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Demo Modal - Shows when user sends a question */}
      {showDemoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#F0F2FF] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              {storedQuestion && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4 text-left">
                  <p className="text-xs text-gray-500 mb-1">Your question:</p>
                  <p className="text-sm text-gray-700">{storedQuestion}</p>
                </div>
              )}
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Want to see Flashgate in action?
              </h3>
              <p className="text-gray-600 mb-2 text-sm">
                Book a quick demo call and we'll walk you through how it works for your use case.
              </p>
              <div className="space-y-3 mt-6">
                <button
                  onClick={() => navigate('/auth?tab=signup')}
                  className="w-full px-6 py-3 text-sm font-medium text-white bg-[#617BFF] rounded-lg hover:bg-[#5068E0] transition-colors"
                >
                  Create Free Account
                </button>
                <button
                  onClick={async () => {
                    const win = window.open('', '_blank');
                    try {
                      if (apiClient) {
                        const response = await apiClient.requestDemo({
                          userId: '',
                          email: '',
                          name: '',
                        });
                        if (response?.success && response?.calendarUrl) {
                          if (win) win.location.href = response.calendarUrl;
                          else window.open(response.calendarUrl, '_blank');
                        } else if (win) win.close();
                      } else if (win) win.close();
                    } catch (error) {
                      console.error('Error requesting demo:', error);
                      if (win) win.close();
                    }
                    setShowDemoModal(false);
                  }}
                  className="w-full px-6 py-3 text-sm font-medium text-[#617BFF] bg-[#F0F2FF] rounded-lg hover:bg-[#E3E8FF] transition-colors"
                >
                  Request a Demo
                </button>
                <button
                  onClick={() => setShowDemoModal(false)}
                  className="w-full px-6 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Poke Modal - Follow-up popup after interaction */}
      {showPokeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#F0F2FF] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <p className="text-gray-700 mb-6 text-sm leading-relaxed">
                {pokeMessage}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/auth?tab=signup')}
                  className="w-full px-6 py-3 text-sm font-medium text-white bg-[#617BFF] rounded-lg hover:bg-[#5068E0] transition-colors"
                >
                  Create Free Account
                </button>
                <button
                  onClick={handlePokeRequestDemo}
                  className="w-full px-6 py-3 text-sm font-medium text-[#617BFF] bg-[#F0F2FF] rounded-lg hover:bg-[#E3E8FF] transition-colors"
                >
                  Request a Demo
                </button>
                <button
                  onClick={handlePokeCancel}
                  className="w-full px-6 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signup Modal */}
      {showSignupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#F0F2FF] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Want to continue the conversation?
              </h3>
              <p className="text-gray-600 mb-6">
                Create a free account to get personalized help and start optimizing your cloud storage today.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/auth?tab=signup')}
                  className="w-full px-6 py-3 text-sm font-medium text-white bg-[#617BFF] rounded-lg hover:bg-[#5068E0] transition-colors"
                >
                  Create Free Account
                </button>
                <button
                  onClick={() => navigate('/auth')}
                  className="w-full px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={() => setShowSignupModal(false)}
                  className="w-full px-6 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPageV2;
